import { supabase } from '../supabaseClient';

export type DutyKind   = 'fixed_post' | 'patrol' | 'desk' | 'mobile' | 'custom';
export type DutyColour = 'sky' | 'mint' | 'amber' | 'rose' | 'slate';

export interface LicenceTypeSummary {
  id: string;
  name: string;
  country_code: string | null;
}

export interface LicenceTypeRow extends LicenceTypeSummary {
  authority: string;
  is_active: boolean;
}

export interface SiteRow {
  id: string;
  name: string;
  client_contract_id: string | null;
  client_contract_name: string | null;
  client_contract_status?: string | null;
  country_code: string | null;
  timezone: string | null;
  required_coverage_start_minute: number | null;
  required_coverage_end_minute: number | null;
  required_guard_count: number;
  required_licence_type_id: string | null;
  required_licence: LicenceTypeSummary | null;
}

export interface GuardRow {
  id:         string;
  full_name:  string;
  avatar_url: string | null;
}

/** The clients and sites a guard is bound to. A guard bound to nothing has no row. */
export interface GuardSiteBindingRow {
  guard_id:            string;
  client_contract_ids: string[];
  site_ids:            string[];
}

/**
 * Whether a guard's binding keeps them off a site: they are bound to something,
 * and it is neither this site nor its client. The database's own answer is
 * guard_can_work_site, which auto scheduling is judged by.
 */
export const isBoundElsewhere = (
  binding: GuardSiteBindingRow | undefined,
  site: Pick<SiteRow, 'id' | 'client_contract_id'>,
): boolean => {
  if (!binding) return false;
  if (binding.site_ids.includes(site.id)) return false;
  return !(site.client_contract_id && binding.client_contract_ids.includes(site.client_contract_id));
};

export interface PatrolRow {
  id:                          string;
  name:                        string;
  description:                 string | null;
  estimated_duration_minutes:  number | null;
  is_loop:                     boolean;
}

export interface PostRow {
  id:          string;
  name:        string;
  description: string | null;
}

export interface DutySlotAssignment {
  id:         string;
  guard_id:   string;
  guard_name: string;
  avatar_url: string | null;
}

export interface BreakSlotAssignment {
  id:         string;
  guard_id:   string;
  guard_name: string;
  avatar_url: string | null;
}

export interface BreakSlot {
  id:               string;
  company_id:       string;
  site_id:          string;
  slot_date:        string;
  duration_minutes: number;
  is_paid:          boolean;
  start_minute:     number;
  end_minute:       number;
  display_order:    number;
  is_published:     boolean;
  notes:            string | null;
  assignments:      BreakSlotAssignment[];
}

export interface DutySlot {
  id:              string;
  company_id:      string;
  site_id:         string;
  slot_date:       string;
  name:            string;
  duty_kind:       DutyKind;
  colour:          DutyColour;
  start_minute:    number;
  end_minute:      number;
  guards_required: number;
  is_published:    boolean;
  notes:           string | null;
  display_order:   number;
  patrol_id:       string | null;
  post_id:         string | null;
  required_licence_type_id: string | null;
  required_licence: LicenceTypeSummary | null;
  assignments:     DutySlotAssignment[];
}

export interface GuardLicenceRow {
  guard_id: string;
  licence_type_id: string | null;
  status: string;
  expiry_date: string;
}

export interface PublishedShiftAssignmentRow {
  guard_id: string;
  assignment_status: string;
  shift_id: string;
  shift_title: string | null;
  shift_date: string;
  shift_start_minute: number;
  shift_end_minute: number;
  shift_site_name: string | null;
}

export interface RosterRuleViolation {
  id: string | null;
  slot_date: string;
  country_code: string;
  roster_rule_id: string;
  rule_code: string;
  rule_name: string;
  severity: 'blocker' | 'warning';
  guard_id: string | null;
  guard_name: string | null;
  shift_id: string | null;
  source_slot_ids: string[];
  message: string;
  details: Record<string, unknown>;
  is_overridden: boolean;
  overridden_by: string | null;
  overridden_at: string | null;
  created_at: string | null;
}

export interface PublishRosterDayResult {
  status: 'published' | 'blocked';
  overridden: boolean;
  violations: RosterRuleViolation[];
}

// ── Internal helpers ──────────────────────────────────────────────────────────

let _companyIdCache: string | null = null;

async function getCompanyId(): Promise<string> {
  if (_companyIdCache) return _companyIdCache;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('profiles')
    .select('company_id')
    .eq('id', user.id)
    .single();
  if (error) throw error;
  _companyIdCache = data.company_id as string;
  return _companyIdCache;
}

function toLicenceSummary(value: unknown): LicenceTypeSummary | null {
  const record = Array.isArray(value) ? value[0] : value;
  if (!record || typeof record !== 'object') return null;

  return {
    id: (record as { id: string }).id,
    name: (record as { name: string }).name,
    country_code: ((record as { country_code?: string | null }).country_code ?? null),
  };
}

function toSiteRow(row: Record<string, unknown>): SiteRow {
  const clientContract = Array.isArray(row.client_contract) ? row.client_contract[0] : row.client_contract;
  return {
    id: row.id as string,
    name: row.name as string,
    client_contract_id: (row.client_contract_id as string | null) ?? null,
    client_contract_name: ((clientContract as { name?: string | null } | null)?.name ?? null),
    client_contract_status: ((clientContract as { status?: string | null } | null)?.status ?? null),
    country_code: (row.country_code as string | null) ?? null,
    timezone: (row.timezone as string | null) ?? null,
    required_coverage_start_minute: (row.required_coverage_start_minute as number | null) ?? null,
    required_coverage_end_minute: (row.required_coverage_end_minute as number | null) ?? null,
    required_guard_count: (row.required_guard_count as number | null) ?? 1,
    required_licence_type_id: (row.required_licence_type_id as string | null) ?? null,
    required_licence: toLicenceSummary(row.required_licence),
  };
}

// Map raw DB row + nested assignments to our clean type
function toSlot(row: Record<string, unknown>): DutySlot {
  const rawAssignments = (row.assignments as Array<{
    id: string;
    guard_id: string;
    guard: { full_name: string; avatar_url: string | null } | null;
  }>) ?? [];

  return {
    id:              row.id as string,
    company_id:      row.company_id as string,
    site_id:         row.site_id as string,
    slot_date:       row.slot_date as string,
    name:            row.name as string,
    duty_kind:       row.duty_kind as DutyKind,
    colour:          row.colour as DutyColour,
    start_minute:    row.start_minute as number,
    end_minute:      row.end_minute as number,
    guards_required: row.guards_required as number,
    is_published:    row.is_published as boolean,
    notes:           (row.notes as string) ?? null,
    display_order:   (row.display_order as number) ?? 0,
    patrol_id:       (row.patrol_id as string) ?? null,
    post_id:         (row.post_id as string) ?? null,
    required_licence_type_id: (row.required_licence_type_id as string) ?? null,
    required_licence: toLicenceSummary(row.required_licence),
    assignments: rawAssignments.map((a) => ({
      id:         a.id,
      guard_id:   a.guard_id,
      guard_name: a.guard?.full_name ?? 'Unknown',
      avatar_url: a.guard?.avatar_url ?? null,
    })),
  };
}

const ASSIGNMENT_SELECT = `
  id,
  guard_id,
  guard:profiles!duty_slot_assignments_guard_id_fkey(full_name, avatar_url)
`;

const BREAK_ASSIGNMENT_SELECT = `
  id,
  guard_id,
  guard:profiles!break_slot_assignments_guard_id_fkey(full_name, avatar_url)
`;

const DUTY_SLOT_SELECT = `
  *,
  required_licence:licence_types!duty_slots_required_licence_type_id_fkey(
    id,
    name,
    country_code
  ),
  assignments:duty_slot_assignments(${ASSIGNMENT_SELECT})
`;

function toBreakSlot(row: Record<string, unknown>): BreakSlot {
  const rawAssignments = (row.assignments as Array<{
    id: string;
    guard_id: string;
    guard: { full_name: string; avatar_url: string | null } | null;
  }>) ?? [];

  return {
    id:               row.id as string,
    company_id:       row.company_id as string,
    site_id:          row.site_id as string,
    slot_date:        row.slot_date as string,
    duration_minutes: row.duration_minutes as number,
    is_paid:          row.is_paid as boolean,
    start_minute:     row.start_minute as number,
    end_minute:       row.end_minute as number,
    display_order:    (row.display_order as number) ?? 0,
    is_published:     row.is_published as boolean,
    notes:            (row.notes as string) ?? null,
    assignments: rawAssignments.map((a) => ({
      id:         a.id,
      guard_id:   a.guard_id,
      guard_name: a.guard?.full_name ?? 'Unknown',
      avatar_url: a.guard?.avatar_url ?? null,
    })),
  };
}

export function toRosterRuleViolation(row: Record<string, unknown>): RosterRuleViolation {
  return {
    id: (row.id as string | null) ?? null,
    slot_date: row.slot_date as string,
    country_code: ((row.country_code as string | null) ?? '').trim(),
    roster_rule_id: row.roster_rule_id as string,
    rule_code: row.rule_code as string,
    rule_name: row.rule_name as string,
    severity: row.severity === 'warning' ? 'warning' : 'blocker',
    guard_id: (row.guard_id as string | null) ?? null,
    guard_name: (row.guard_name as string | null) ?? null,
    shift_id: (row.shift_id as string | null) ?? null,
    source_slot_ids: Array.isArray(row.source_slot_ids) ? row.source_slot_ids as string[] : [],
    message: row.message as string,
    details: (row.details && typeof row.details === 'object' ? row.details : {}) as Record<string, unknown>,
    is_overridden: Boolean(row.is_overridden),
    overridden_by: (row.overridden_by as string | null) ?? null,
    overridden_at: (row.overridden_at as string | null) ?? null,
    created_at: (row.created_at as string | null) ?? null,
  };
}

function toPublishRosterDayResult(value: unknown): PublishRosterDayResult {
  const record = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const rawViolations = Array.isArray(record.violations) ? record.violations : [];
  const overridden = Boolean(record.overridden);

  return {
    status: record.status === 'blocked' ? 'blocked' : 'published',
    overridden,
    violations: rawViolations.map((violation) => {
      const mappedViolation = toRosterRuleViolation(violation as Record<string, unknown>);
      return {
        ...mappedViolation,
        is_overridden: mappedViolation.is_overridden || overridden,
      };
    }),
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

export const dutyBoardService = {
  /** Sites for the authenticated user's company */
  async fetchSites(): Promise<SiteRow[]> {
    const companyId = await getCompanyId();
    const { data, error } = await supabase
      .from('sites')
      .select(`
        id,
        name,
        client_contract_id,
        client_contract:client_contracts!sites_client_contract_id_fkey(
          name,
          status
        ),
        country_code,
        timezone,
        required_coverage_start_minute,
        required_coverage_end_minute,
        required_guard_count,
        required_licence_type_id,
        required_licence:licence_types!sites_required_licence_type_id_fkey(
          id,
          name,
          country_code
        )
      `)
      .eq('company_id', companyId)
      .order('name');
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[])
      .map(toSiteRow)
      .filter((site) => site.client_contract_status !== 'archived');
  },

  /** Everybody who holds a guard role, including a guard who also manages. */
  async fetchGuards(): Promise<GuardRow[]> {
    const companyId = await getCompanyId();
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url')
      .eq('company_id', companyId)
      .eq('is_guard', true)
      .order('full_name');
    if (error) throw error;
    return (data ?? []) as GuardRow[];
  },

  async fetchLicenceTypes(countryCode: string): Promise<LicenceTypeRow[]> {
    const trimmedCountryCode = countryCode.trim().toUpperCase();
    if (!trimmedCountryCode) return [];

    const { data, error } = await supabase
      .from('licence_types')
      .select('id, name, country_code, authority, is_active')
      .eq('country_code', trimmedCountryCode)
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) throw error;
    return (data ?? []) as LicenceTypeRow[];
  },

  /** Guards bound to particular clients or sites; everybody else can be rostered anywhere. */
  async fetchGuardSiteBindings(): Promise<GuardSiteBindingRow[]> {
    const { data, error } = await supabase.rpc('get_guard_site_bindings');
    if (error) throw error;
    const ids = (value: unknown): string[] => (
      Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : []
    );
    return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
      guard_id: row.guard_id as string,
      client_contract_ids: ids(row.client_contract_ids),
      site_ids: ids(row.site_ids),
    }));
  },

  async fetchCompanyGuardLicences(): Promise<GuardLicenceRow[]> {
    const companyId = await getCompanyId();
    const { data, error } = await supabase
      .from('guard_licences')
      .select('guard_id, licence_type_id, status, expiry_date')
      .eq('company_id', companyId);

    if (error) throw error;
    return (data ?? []) as GuardLicenceRow[];
  },

  async fetchPublishedShiftAssignments(startDate: string, endDate: string): Promise<PublishedShiftAssignmentRow[]> {
    const companyId = await getCompanyId();
    const { data: slotData, error: slotError } = await supabase
      .from('duty_slots')
      .select('id, name, slot_date, start_minute, end_minute, site_id, is_published')
      .eq('company_id', companyId)
      .eq('is_published', true)
      .gte('slot_date', startDate)
      .lte('slot_date', endDate)
      .order('slot_date', { ascending: true })
      .order('start_minute', { ascending: true });

    if (slotError) throw slotError;

    const slots = (slotData ?? []) as Array<{
      id: string;
      name: string | null;
      slot_date: string;
      start_minute: number;
      end_minute: number;
      site_id: string | null;
      is_published: boolean;
    }>;

    if (slots.length === 0) return [];

    const slotIds = slots.map((slot) => slot.id);
    const slotMap = new Map(slots.map((slot) => [slot.id, slot]));

    const { data: assignmentData, error: assignmentError } = await supabase
      .from('duty_slot_assignments')
      .select('guard_id, duty_slot_id')
      .eq('company_id', companyId)
      .in('duty_slot_id', slotIds);

    if (assignmentError) throw assignmentError;

    const assignments = (assignmentData ?? []) as Array<{
      guard_id: string | null;
      duty_slot_id: string | null;
    }>;

    return assignments
      .map((assignment) => {
        const dutySlot = assignment.duty_slot_id ? slotMap.get(assignment.duty_slot_id) ?? null : null;
        return {
          guard_id: assignment.guard_id,
          duty_slot: dutySlot,
        };
      })
      .filter((assignment) => Boolean(assignment.guard_id) && Boolean(assignment.duty_slot))
      .map((assignment) => ({
        guard_id: assignment.guard_id!,
        assignment_status: 'assigned',
        shift_id: assignment.duty_slot!.id,
        shift_title: assignment.duty_slot!.name ?? null,
        shift_date: assignment.duty_slot!.slot_date,
        shift_start_minute: assignment.duty_slot!.start_minute,
        shift_end_minute: assignment.duty_slot!.end_minute,
        shift_site_name: null,
      }));
  },

  /** All duty slots for one site + date, with their assignments */
  async fetchSlots(siteId: string, slotDate: string): Promise<DutySlot[]> {
    const { data, error } = await supabase
      .from('duty_slots')
      .select(DUTY_SLOT_SELECT)
      .eq('site_id', siteId)
      .eq('slot_date', slotDate)
      .order('display_order')
      .order('start_minute');
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(toSlot);
  },

  /** Create a new duty slot; returns the full record with assignments (empty) */
  async createSlot(params: {
    siteId:         string;
    slotDate:       string;
    name:           string;
    dutyKind:       DutyKind;
    colour:         DutyColour;
    startMinute:    number;
    endMinute:      number;
    guardsRequired: number;
    displayOrder:   number;
    requiredLicenceTypeId?: string | null;
    patrolId?:      string | null;
    postId?:        string | null;
    isPublished?:   boolean;
    notes?:         string | null;
  }): Promise<DutySlot> {
    const companyId = await getCompanyId();
    const { data, error } = await supabase
      .from('duty_slots')
      .insert({
        company_id:      companyId,
        site_id:         params.siteId,
        slot_date:       params.slotDate,
        name:            params.name,
        duty_kind:       params.dutyKind,
        colour:          params.colour,
        start_minute:    params.startMinute,
        end_minute:      params.endMinute,
        guards_required: params.guardsRequired,
        display_order:   params.displayOrder,
        required_licence_type_id: params.requiredLicenceTypeId ?? null,
        patrol_id:       params.patrolId ?? null,
        post_id:         params.postId ?? null,
        is_published:    params.isPublished ?? false,
        notes:           params.notes ?? null,
      })
      .select(DUTY_SLOT_SELECT)
      .single();
    if (error) throw error;
    return toSlot(data as Record<string, unknown>);
  },

  /** Update any fields of a slot (partial) */
  async updateSlot(
    slotId: string,
    updates: Partial<{
      name:            string;
      duty_kind:       DutyKind;
      colour:          DutyColour;
      start_minute:    number;
      end_minute:      number;
      guards_required: number;
      is_published:    boolean;
      notes:           string | null;
      patrol_id:       string | null;
      post_id:         string | null;
      required_licence_type_id: string | null;
    }>,
  ): Promise<void> {
    const { error } = await supabase
      .from('duty_slots')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', slotId);
    if (error) throw error;
  },

  /** Hard-delete a slot (cascades to assignments) */
  async deleteSlot(slotId: string): Promise<void> {
    const { error } = await supabase.from('duty_slots').delete().eq('id', slotId);
    if (error) throw error;
  },

  /** Assign a guard to a slot; returns the new assignment row */
  async assignGuard(slotId: string, guardId: string): Promise<DutySlotAssignment> {
    const companyId = await getCompanyId();
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('duty_slot_assignments')
      .insert({
        company_id:   companyId,
        duty_slot_id: slotId,
        guard_id:     guardId,
        assigned_by:  user?.id ?? null,
      })
      .select(`id, guard_id, guard:profiles!duty_slot_assignments_guard_id_fkey(full_name, avatar_url)`)
      .single();
    if (error) throw error;
    const row = data as unknown as { id: string; guard_id: string; guard: { full_name: string; avatar_url: string | null } | null };
    return { id: row.id, guard_id: row.guard_id, guard_name: row.guard?.full_name ?? 'Unknown', avatar_url: row.guard?.avatar_url ?? null };
  },

  /** Remove a guard from a slot */
  async unassignGuard(assignmentId: string): Promise<void> {
    const { error } = await supabase
      .from('duty_slot_assignments')
      .delete()
      .eq('id', assignmentId);
    if (error) throw error;
  },

  /** Patrols for a site */
  async fetchPatrols(siteId: string): Promise<PatrolRow[]> {
    const { data, error } = await supabase
      .from('patrols')
      .select('id, name, description, estimated_duration_minutes, is_loop')
      .eq('site_id', siteId)
      .order('name');
    if (error) throw error;
    return (data ?? []) as PatrolRow[];
  },

  /** Posts for a site */
  async fetchPosts(siteId: string): Promise<PostRow[]> {
    const { data, error } = await supabase
      .from('posts')
      .select('id, name, description')
      .eq('site_id', siteId)
      .order('name');
    if (error) throw error;
    return (data ?? []) as PostRow[];
  },

  /** Validate and publish every duty slot AND break slot on a site+date */
  async publishDay(
    siteId: string,
    slotDate: string,
    options: { overrideWarnings?: boolean } = {},
  ): Promise<PublishRosterDayResult> {
    const { data, error } = await supabase.rpc('publish_roster_day', {
      p_site_id: siteId,
      p_slot_date: slotDate,
      p_override: Boolean(options.overrideWarnings),
    });

    if (error) throw error;
    return toPublishRosterDayResult(data);
  },

  async fetchRosterRuleViolations(
    siteId: string,
    startDate: string,
    endDate: string,
  ): Promise<RosterRuleViolation[]> {
    const { data, error } = await supabase
      .from('roster_rule_violations')
      .select(`
        id,
        slot_date,
        country_code,
        roster_rule_id,
        rule_code,
        rule_name,
        severity,
        guard_id,
        guard_name,
        shift_id,
        source_slot_ids,
        message,
        details,
        is_overridden,
        overridden_by,
        overridden_at,
        created_at
      `)
      .eq('site_id', siteId)
      .eq('is_current', true)
      .gte('slot_date', startDate)
      .lte('slot_date', endDate)
      .order('slot_date', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(toRosterRuleViolation);
  },

  /** Fetch slots for a date range (inclusive), grouped by date */
  async fetchSlotsRange(siteId: string, startDate: string, endDate: string): Promise<Record<string, DutySlot[]>> {
    const { data, error } = await supabase
      .from('duty_slots')
      .select(DUTY_SLOT_SELECT)
      .eq('site_id', siteId)
      .gte('slot_date', startDate)
      .lte('slot_date', endDate)
      .order('display_order')
      .order('start_minute');
    if (error) throw error;
    const slots = ((data ?? []) as Record<string, unknown>[]).map(toSlot);
    const grouped: Record<string, DutySlot[]> = {};
    for (const slot of slots) {
      if (!grouped[slot.slot_date]) grouped[slot.slot_date] = [];
      grouped[slot.slot_date].push(slot);
    }
    return grouped;
  },

  /** Delete all duty slots AND break slots for a site + date */
  async clearDay(siteId: string, slotDate: string): Promise<void> {
    const { error: dutyErr } = await supabase
      .from('duty_slots')
      .delete()
      .eq('site_id', siteId)
      .eq('slot_date', slotDate);
    if (dutyErr) throw dutyErr;

    const { error: breakErr } = await supabase
      .from('break_slots')
      .delete()
      .eq('site_id', siteId)
      .eq('slot_date', slotDate);
    if (breakErr) throw breakErr;
  },

  /**
   * Copy all duty slots AND break slots from one day to one or more target dates, always as drafts.
   * Guard assignments come along only when `includeAssignments` is set — the caller asks the user first.
   */
  async copyDayTo(
    siteId: string,
    sourceDate: string,
    targetDates: string[],
    options: { includeAssignments?: boolean } = {},
  ): Promise<Record<string, DutySlot[]>> {
    const [sourceSlots, sourceBreaks] = await Promise.all([
      this.fetchSlots(siteId, sourceDate),
      this.fetchBreakSlots(siteId, sourceDate),
    ]);
    if ((sourceSlots.length === 0 && sourceBreaks.length === 0) || targetDates.length === 0) return {};

    const companyId = await getCompanyId();
    const includeAssignments = options.includeAssignments === true;
    // Two blocks in one row can never overlap, so row + start minute identifies a copy's source.
    const sourceKey = (displayOrder: number, startMinute: number) => `${displayOrder}|${startMinute}`;
    const assignedBy = includeAssignments
      ? (await supabase.auth.getUser()).data.user?.id ?? null
      : null;

    // Copy duty slots
    const grouped: Record<string, DutySlot[]> = {};
    if (sourceSlots.length > 0) {
      const dutyInserts = targetDates.flatMap((targetDate) =>
        sourceSlots.map((slot) => ({
          company_id: companyId,
          site_id: siteId,
          slot_date: targetDate,
          name: slot.name,
          duty_kind: slot.duty_kind,
          colour: slot.colour,
          start_minute: slot.start_minute,
          end_minute: slot.end_minute,
          guards_required: slot.guards_required,
          display_order: slot.display_order,
          required_licence_type_id: slot.required_licence_type_id,
          is_published: false,
          notes: slot.notes,
          patrol_id: slot.patrol_id,
          post_id: slot.post_id,
        })),
      );

      const { data, error } = await supabase
        .from('duty_slots')
        .insert(dutyInserts)
        .select(DUTY_SLOT_SELECT);
      if (error) throw error;

      const created = ((data ?? []) as Record<string, unknown>[]).map(toSlot);
      for (const slot of created) {
        if (!grouped[slot.slot_date]) grouped[slot.slot_date] = [];
        grouped[slot.slot_date].push(slot);
      }

      if (includeAssignments) {
        const guardIdsBySource = new Map<string, string[]>();
        for (const slot of sourceSlots) {
          if (slot.assignments.length === 0) continue;
          guardIdsBySource.set(
            sourceKey(slot.display_order, slot.start_minute),
            slot.assignments.map((assignment) => assignment.guard_id),
          );
        }

        const assignmentInserts = created.flatMap((slot) => (
          (guardIdsBySource.get(sourceKey(slot.display_order, slot.start_minute)) ?? []).map((guardId) => ({
            company_id:   companyId,
            duty_slot_id: slot.id,
            guard_id:     guardId,
            assigned_by:  assignedBy,
          }))
        ));

        if (assignmentInserts.length > 0) {
          const { data: assignmentData, error: assignmentError } = await supabase
            .from('duty_slot_assignments')
            .insert(assignmentInserts)
            .select(`duty_slot_id, ${ASSIGNMENT_SELECT}`);
          if (assignmentError) throw assignmentError;

          const bySlotId = new Map<string, DutySlotAssignment[]>();
          for (const raw of (assignmentData ?? []) as unknown as Array<{
            duty_slot_id: string;
            id: string;
            guard_id: string;
            guard: { full_name: string; avatar_url: string | null } | null;
          }>) {
            const list = bySlotId.get(raw.duty_slot_id) ?? [];
            list.push({
              id:         raw.id,
              guard_id:   raw.guard_id,
              guard_name: raw.guard?.full_name ?? 'Unknown',
              avatar_url: raw.guard?.avatar_url ?? null,
            });
            bySlotId.set(raw.duty_slot_id, list);
          }

          for (const dateKey of Object.keys(grouped)) {
            grouped[dateKey] = grouped[dateKey].map((slot) => ({
              ...slot,
              assignments: bySlotId.get(slot.id) ?? slot.assignments,
            }));
          }
        }
      }
    }

    // Copy break slots
    if (sourceBreaks.length > 0) {
      const breakInserts = targetDates.flatMap((targetDate) =>
        sourceBreaks.map((bs) => ({
          company_id: companyId,
          site_id: siteId,
          slot_date: targetDate,
          duration_minutes: bs.duration_minutes,
          is_paid: bs.is_paid,
          start_minute: bs.start_minute,
          end_minute: bs.end_minute,
          display_order: bs.display_order,
          is_published: false,
          notes: bs.notes,
        })),
      );

      const { data: breakData, error: breakErr } = await supabase
        .from('break_slots')
        .insert(breakInserts)
        .select('id, display_order, start_minute');
      if (breakErr) throw breakErr;

      if (includeAssignments) {
        const guardIdsBySource = new Map<string, string[]>();
        for (const breakSlot of sourceBreaks) {
          if (breakSlot.assignments.length === 0) continue;
          guardIdsBySource.set(
            sourceKey(breakSlot.display_order, breakSlot.start_minute),
            breakSlot.assignments.map((assignment) => assignment.guard_id),
          );
        }

        const breakAssignmentInserts = ((breakData ?? []) as unknown as Array<{
          id: string;
          display_order: number;
          start_minute: number;
        }>).flatMap((row) => (
          (guardIdsBySource.get(sourceKey(row.display_order, row.start_minute)) ?? []).map((guardId) => ({
            company_id:    companyId,
            break_slot_id: row.id,
            guard_id:      guardId,
            assigned_by:   assignedBy,
          }))
        ));

        if (breakAssignmentInserts.length > 0) {
          const { error: breakAssignErr } = await supabase
            .from('break_slot_assignments')
            .insert(breakAssignmentInserts);
          if (breakAssignErr) throw breakAssignErr;
        }
      }
    }

    return grouped;
  },

  // ── Break slots ──────────────────────────────────────────────────────────

  async fetchBreakSlots(siteId: string, slotDate: string): Promise<BreakSlot[]> {
    const { data, error } = await supabase
      .from('break_slots')
      .select(`*, assignments:break_slot_assignments(${BREAK_ASSIGNMENT_SELECT})`)
      .eq('site_id', siteId)
      .eq('slot_date', slotDate)
      .order('display_order')
      .order('start_minute');
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(toBreakSlot);
  },

  async createBreakSlot(params: {
    siteId:          string;
    slotDate:        string;
    durationMinutes: number;
    isPaid:          boolean;
    startMinute:     number;
    endMinute:       number;
    displayOrder:    number;
  }): Promise<BreakSlot> {
    const companyId = await getCompanyId();
    const { data, error } = await supabase
      .from('break_slots')
      .insert({
        company_id:       companyId,
        site_id:          params.siteId,
        slot_date:        params.slotDate,
        duration_minutes: params.durationMinutes,
        is_paid:          params.isPaid,
        start_minute:     params.startMinute,
        end_minute:       params.endMinute,
        display_order:    params.displayOrder,
        is_published:     false,
      })
      .select(`*, assignments:break_slot_assignments(${BREAK_ASSIGNMENT_SELECT})`)
      .single();
    if (error) throw error;
    return toBreakSlot(data as Record<string, unknown>);
  },

  async updateBreakSlot(
    breakSlotId: string,
    updates: Partial<{
      start_minute:     number;
      end_minute:       number;
      duration_minutes: number;
      is_paid:          boolean;
      is_published:     boolean;
      notes:            string | null;
    }>,
  ): Promise<void> {
    const { error } = await supabase
      .from('break_slots')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', breakSlotId);
    if (error) throw error;
  },

  async deleteBreakSlot(breakSlotId: string): Promise<void> {
    const { error } = await supabase.from('break_slots').delete().eq('id', breakSlotId);
    if (error) throw error;
  },

  async assignGuardToBreak(breakSlotId: string, guardId: string): Promise<BreakSlotAssignment> {
    const companyId = await getCompanyId();
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('break_slot_assignments')
      .insert({
        company_id:    companyId,
        break_slot_id: breakSlotId,
        guard_id:      guardId,
        assigned_by:   user?.id ?? null,
      })
      .select(`id, guard_id, guard:profiles!break_slot_assignments_guard_id_fkey(full_name, avatar_url)`)
      .single();
    if (error) throw error;
    const row = data as unknown as { id: string; guard_id: string; guard: { full_name: string; avatar_url: string | null } | null };
    return { id: row.id, guard_id: row.guard_id, guard_name: row.guard?.full_name ?? 'Unknown', avatar_url: row.guard?.avatar_url ?? null };
  },

  async unassignGuardFromBreak(assignmentId: string): Promise<void> {
    const { error } = await supabase
      .from('break_slot_assignments')
      .delete()
      .eq('id', assignmentId);
    if (error) throw error;
  },

  async publishBreakSlots(siteId: string, slotDate: string): Promise<void> {
    const { error } = await supabase
      .from('break_slots')
      .update({ is_published: true, updated_at: new Date().toISOString() })
      .eq('site_id', siteId)
      .eq('slot_date', slotDate);
    if (error) throw error;
  },
};
